import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * One suggested decision inside the "Suggested decisions" card.
 */
export interface GableSuggestionItem {
    /** e.g. "Leaky irrigation valve" */
    title: string;
    /** e.g. "Maintenance" — small blue/info pill next to the title */
    tag?: string;
    /** e.g. "Unit 112 · Maria Gonzalez · submitted Mon" */
    meta?: string;
    /** Why the AI suggests this — rendered with a sparkle icon */
    reason?: string;
    /** e.g. "Approve" | "Request info" | "Deny" */
    recommendation?: string;
    /** Pill tone for the recommendation (default 'info') */
    tone?: 'positive' | 'info' | 'caution';
    /** e.g. 96 → "96% confident" */
    confidencePct?: number;
    /** Right-side figure, e.g. "$1,480.00" or "3 units" — for money /
     *  metric answer types that aren't decisions. */
    value?: string;
    /** In-app deep link ("/app/requests/123") — makes the title clickable. */
    href?: string;
    /** Machine hint for the pill click: when present, the follow-up prompt
     *  asks the box to propose THIS action with THESE params directly —
     *  deterministic, no re-derivation. Only emitted for actions in the
     *  box's propose catalog. */
    action?: {
        action_type: string;
        params_json: string;
    };
}

/**
 * Payload for the Gable "Suggested decisions" card. The AI streams a
 * fenced JSON block; the drawer host parses it and binds it here.
 */
export interface GableSuggestionCardPayload {
    /** e.g. "Suggested decisions" */
    title: string;
    /** e.g. "5 of 14 need you" — blue pill on the right side of the header */
    badge?: string;
    /** Phosphor icon class, default 'ph-sparkle' */
    icon?: string;
    /** Small gray line under the items */
    footnote?: string;
    items: GableSuggestionItem[];
}

/**
 * Presentational card rendering AI "Suggested decisions" inside the
 * Gable chat drawer (~400-560px wide). v1 is read-only — no checkboxes
 * or action buttons; those are a follow-up.
 */
@Component({
    selector: 'app-gable-suggestion-card',
    templateUrl: './gable-suggestion-card.component.html',
    styleUrls: ['./gable-suggestion-card.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule],
})
export class GableSuggestionCardComponent {
    @Input({ required: true }) payload!: GableSuggestionCardPayload;

    @Output() actionPrompt = new EventEmitter<string>();

    get headerIcon(): string {
        return this.payload?.icon || 'ph-sparkle';
    }

    toneClass(item: GableSuggestionItem): string {
        return `tone-${item.tone ?? 'info'}`;
    }

    onItemAction(item: GableSuggestionItem): void {
        const rec = item.recommendation ?? 'Proceed';
        if (item.action?.action_type) {
            // Structured fast path — the model emitted the exact action +
            // params with the suggestion, so the follow-up is deterministic.
            this.actionPrompt.emit(
                `${rec}: "${item.title}". Propose the ${item.action.action_type} action with exactly these params for my approval: ${item.action.params_json}`,
            );
            return;
        }
        this.actionPrompt.emit(`${rec}: "${item.title}". Propose this action for my approval with a preview I can confirm.`);
    }

    trackByIndex(index: number): number {
        return index;
    }
}
